import React, {memo, MouseEvent, PropsWithChildren, useCallback, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useRPC} from '../../../../hooks/rpc';
import {MenuEntity} from '../../../../store/control/state';
import {ColumnModel, SettingsList} from '../../../../components/Settings/SettingsList';
import {onClickHandler} from '../../../../utils/onclick';
import iconUpArrow from '../../../../assets/images/icons/scenario/up-arrow.png';
import iconDownArrow from '../../../../assets/images/icons/scenario/down-arrow.png';
import {useSortBySelector} from '../../../../hooks/sort-by-selector';
import './index.scss';
import {leftMenuIconList} from '../LeftMenuEditScreen';
import {useLocalization} from "../../../../hooks/localization";

const keyExtractor = (item: MenuEntity) => item.id;

type UpDownProps = {
    index: number;
    onUpClick?: (index: number) => void;
    onDownClick?: (index: number) => void;
};

const UpDown = memo(function UpDown({onUpClick: onUpClickProp, onDownClick: onDownClickProp, index}: UpDownProps) {
    const onUpClick = useCallback((e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        onUpClickProp(index);
    }, [onUpClickProp, index]);

    const onDownClick = useCallback((e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        onDownClickProp(index);
    }, [onDownClickProp, index]);

    return (
        <div className='up-down-button-container'>
            {
                renderIf(onUpClickProp)(() =>
                        <div className='up-down-button' {...onClickHandler(onUpClick)}>
                            <img src={iconUpArrow} alt=''/>
                        </div>,
                    () => <div className='up-down-button'/>,
                )
            }
            {
                renderIf(onDownClickProp)(() =>
                    <div className='up-down-button' {...onClickHandler(onDownClick)}>
                        <img src={iconDownArrow} alt=''/>
                    </div>,
                )
            }
        </div>
    );
});

type MenuElementIconProps = {
    icon: string;
}

const MenuElementIcon = memo(function MenuElementIcon({icon}: MenuElementIconProps) {
        return (
            <div className={'menu-element-icon'}>
                <img src={icon} alt=''/>
            </div>
        );
    },
);

const ColorfulText = memo(function (props: PropsWithChildren<{}>) {
    return (
        <span className="color-red">{props.children}</span>
    );
});

export const LeftMenuListScreen = memo(function HomepageListScreen() {
    const {t} = useLocalization();
    const menuItemList = useSortBySelector(state => state.control.menuList, a => a.order);
    const [rpc] = useRPC();

    const [selectedMenuItem, setSelectedMenuItem] = useState<MenuEntity>(null);

    const buttons = useMemo(() => [
        {
            text: t('edit_content'),
            url: `/settings/left-menu/${selectedMenuItem ? selectedMenuItem.id : ''}/contents/`,
            className: 'blue',
            hidden: !selectedMenuItem || selectedMenuItem.is_special,
        },
        {
            text: t('edit'),
            url: `/settings/left-menu/edit/${selectedMenuItem ? selectedMenuItem.id : ''}`,
            className: 'orange',
            hidden: !(selectedMenuItem),
        },
        {
            text: t('add'),
            url: '/settings/left-menu/edit/',
            className: 'green',
        },
    ], [t, selectedMenuItem]);

    const swapItemOrders = useCallback((itemA: MenuEntity, itemB: MenuEntity) => {
        Promise.all([
            rpc.updateMenu({...itemA, order: itemB.order}),
            rpc.updateMenu({...itemB, order: itemA.order}),
        ]).then(() => setSelectedMenuItem(null));
    }, [rpc]);

    const onUpClick = useCallback((index: number) => {
        swapItemOrders(menuItemList[index - 1], menuItemList[index]);
    }, [swapItemOrders, menuItemList]);

    const onDownClick = useCallback((index: number) => {
        swapItemOrders(menuItemList[index + 1], menuItemList[index]);
    }, [swapItemOrders, menuItemList]);

    const translatedLeftMenuIconList = useMemo(() => leftMenuIconList.map(icon => ({...icon, label: t(icon.label)})), [t]);

    const displayNameExtractor = useCallback((item: MenuEntity, column: ColumnModel, index?: number) => {
        if (column.key === 'order') {
            return <UpDown
                index={index}
                onUpClick={index === 0 ? undefined : onUpClick}
                onDownClick={(index === menuItemList.length - 1) ? undefined : onDownClick}/>;
        }
        if (column.key === 'icon') {
            const icon = translatedLeftMenuIconList.find((i) => i.value.name === item.icon) || null;
            return icon ? <MenuElementIcon icon={icon.value.iconUrl}/> : '-';
        }

        if (column.key === 'name') {
            if (item.is_special) {
                return (
                    <ColorfulText>{item.name}</ColorfulText>
                );
            }
        }

        return item[column.key] || '-';
    }, [onUpClick, menuItemList.length, onDownClick, translatedLeftMenuIconList]);

    const columns = useMemo<ColumnModel[]>(() => [
        {
            length: 3,
            name: t('name'),
            key: 'name',
        },
        {
            length: 1,
            name: t('icon'),
            key: 'icon',
        },
        {
            length: 3,
            name: t('order'),
            key: 'order',
        },
    ], [t]);

    return (
        <PasswordContainer requiredRole='service' isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                backUrl='/settings'>
                <SettingsList
                    columns={columns}
                    items={menuItemList}
                    selectedItem={selectedMenuItem}
                    onItemSelected={setSelectedMenuItem}
                    displayNameExtractor={displayNameExtractor}
                    keyExtractor={keyExtractor}
                    sortDisabled={true}/>
            </SettingsPageContainer>
        </PasswordContainer>
    );
});
